/* eslint-disable react/display-name */
import React, { memo, useState } from 'react';
import PropTypes from 'prop-types';

import { utilities } from '@weatherbug/common';
import { reduxConnect } from '../../common';
import { useInterval } from './../../common/hooks/useInterval';
import { ResortCardView } from './resortCardView';
import { ResortDetails } from './resortDetails';

const {
    lengthUnitAbbreviations,
    speedUnitsEnum,
    windUnit, 
    precipitationUnit,
    temperatureUnitAbbreviations,
    unitSystems
} = utilities.units;
const locationType = 'latitudelongitude';
const offset = 0;
const units = unitSystems.english;
const verbose = false;
const refresh = false;
// TECHDEBT put these 3 values in a config setting somewhere
const hourlyInterval = 60 * 60 * 1000;
const dailyInterval = 24 * 60 * 60 * 1000;
const obsInterval = 5 * 60 * 1000;
const getHourlyForLength = 1; // gets hourly forecast for 1hour, if needed for 5hrs value should be 5 etc.
const roundingProfile = 'DisplayV1';
const cacheDuration = 60 * 1000 * 15;

const mapStoreStateToProps = storeState => {
    const appState = storeState.appState.appState || {};
    const { user, routeParams } = appState || {};

    const { SlugName: slugName } = user?.ActiveLocation?.Locality || {};
    const { center: mapCenterLocation } = routeParams || {};

    return {
        slugName,
        mapCenterLocation
    };
};

const mapDispatchToProps = {};

export const ResortCard = reduxConnect( mapStoreStateToProps, mapDispatchToProps )(memo(({
    index,
    resortName,
    baseSnow,
    newSnow,
    reported,
    openStatusFlag,
    openStatus,
    totalLifts,
    openLifts,
    snowCondition,
    forecastBaseUrl,
    contentMgmtBaseUrl,
    forecastIconResourceList,
    forecastIconSize,
    forecastIconSet,
    resortLatitude,
    resortLongitude,
    authId,
    secret,
    obsBaseUrl,
    stdParameters,
    user,
    getHourlyForecastByLatLon,
    getDailyForecastByLatLon,
    getCurrentStationObsByLatLon,
    showResortView,
    tempUnit,
    summitTemp,
    baseTemp,
    summitVisibility,
    baseVisibility,
    summitWindGust,
    baseWindGust,
    openTrails,
    totalTrails,
    summit,
    base,
    verticalDrop,
    nordic,
    skiing,
    snowPark,
    indoorResort,
    glacierResort,
    summerActivities,
    street,
    city,
    stateName,
    zip,
    phone800,
    website,
    slugName,
    mapCenterLocation
}) => {
    const [hourlyData, setHourlyData] = useState();
    const [iconCode, setIconCode] = useState();
    const [dailyData, setDailyData] = useState();
    const [highTemp, setHighTemp] = useState();
    const [lowTemp, setLowTemp] = useState();
    const [observationData, setObservationData] = useState();
    const [temperature, setTemperature] = useState();
    const [feelsLike, setFeelsLike] = useState();

    const resortHref = `/life/snow-ski-detail/${slugName}?center=${mapCenterLocation}&index=${index}`;
    const tempUnitAbbreviation = temperatureUnitAbbreviations[tempUnit];
    const windSpeedUnit = user?.WindUnit;
    const windSpeedUnitAbbreviation = speedUnitsEnum[windSpeedUnit];
    const pressureUnit = user?.PressureUnit;
    const lengthUnit = tempUnit;
    const lengthUnitAbbreviation = lengthUnitAbbreviations[lengthUnit];

    const basePropsData = {
        resortHref,
        index,
        resortName,
        baseSnow,
        newSnow,
        reported,
        openStatusFlag,
        openStatus,
        totalLifts,
        openLifts,
        snowCondition,
        contentMgmtBaseUrl,
        forecastIconResourceList,
        forecastIconSize,
        forecastIconSet,
        authId,
        secret,
        stdParameters,
        temperature,
        iconCode,
        feelsLike,
        highTemp,
        lowTemp,
        tempUnitAbbreviation
    };

    const detailsPropsData = {
        summitTemp,
        baseTemp,
        summitVisibility,
        baseVisibility,
        summitWindGust,
        baseWindGust,
        openTrails,
        totalTrails,
        summit,
        base,
        verticalDrop,
        nordic,
        skiing,
        snowPark,
        indoorResort,
        glacierResort,
        summerActivities,
        street,
        city,
        stateName,
        zip,
        phone800,
        website,
        resortLatitude,
        resortLongitude,
        tempUnit,
        windSpeedUnitAbbreviation,
        pressureUnit,
        lengthUnitAbbreviation
    };

    useInterval(
        () => {
            try {
                getHourlyForecastByLatLon({
                    location: `${resortLatitude},${resortLongitude}`,
                    locationType,
                    offset,
                    length: getHourlyForLength,
                    baseUrl: forecastBaseUrl,
                    authId,
                    secret,
                    stdParameters,
                    verbose,
                    refresh,
                    temperatureUnit: tempUnit,
                    pressureUnit,
                    speedUnit: windUnit,
                    precipitationUnit,
                    roundingProfile,
                    cacheDuration
                }).then(data => {
                    setHourlyData(data?.value?.hf?.hfp?.[0]);
                    setIconCode(data?.value?.hf?.hfp?.[0]?.ic);
                });
            } catch (error) {
                console.log(error);
            }
        },
        hourlyInterval,
        true,
        !!(authId && secret && stdParameters && forecastBaseUrl && resortLatitude && resortLongitude && tempUnit),
        [resortLatitude, resortLongitude, tempUnit]
    );

    useInterval(
        () => {
            try {
                getDailyForecastByLatLon(
                    `${resortLatitude},${resortLongitude}`,
                    locationType,
                    offset,
                    units,
                    forecastBaseUrl,
                    authId,
                    secret,
                    stdParameters,
                    verbose,
                    refresh,
                    tempUnit
                ).then(data => {
                    setDailyData(data?.value?.dfp?.[0]);
                    setHighTemp(data?.value?.dfp?.[0]?.t);
                    setLowTemp(data?.value?.dfp?.[1]?.t);
                });
            } catch (error) {
                console.log(error);
            }
        },
        dailyInterval,
        true,
        !!(authId && secret && stdParameters && forecastBaseUrl && resortLatitude && resortLongitude && tempUnit),
        [resortLatitude, resortLongitude, tempUnit]
    );

    useInterval(
        () => {
            try {
                getCurrentStationObsByLatLon(
                    `${resortLatitude},${resortLongitude}`,
                    locationType,
                    offset,
                    units,
                    obsBaseUrl,
                    authId,
                    secret,
                    stdParameters,
                    verbose,
                    refresh,
                    lengthUnit,
                    tempUnit,
                    pressureUnit,
                    windSpeedUnit
                ).then(data => {
                    setObservationData(data?.value);
                    setTemperature(Math.round(data?.value?.o?.t));
                    setFeelsLike(Math.round(data?.value?.o?.fl));
                });
            } catch (error) {
                console.log(error);
            }
        },
        obsInterval,
        true,
        !!(authId && secret && stdParameters && forecastBaseUrl && resortLatitude && resortLongitude && tempUnit),
        [resortLatitude, resortLongitude, tempUnit]
    );

    if (!hourlyData || !dailyData || !observationData) {
        return null;
    }
    if (showResortView) {
        return <ResortCardView {...basePropsData} />;
    } else {
        return (
            <ResortDetails {...basePropsData} {...detailsPropsData} />
        );
    }
}));

ResortCard.displayName = 'ResortCard';
ResortCard.propTypes = {
    index: PropTypes.number,
    resortName: PropTypes.string,
    baseSnow: PropTypes.any,
    newSnow: PropTypes.any,
    reported: PropTypes.string,
    openStatusFlag: PropTypes.number,
    openStatus: PropTypes.string,
    totalLifts: PropTypes.any,
    openLifts: PropTypes.any,
    snowCondition: PropTypes.string,
    resortLatitude: PropTypes.number,
    resortLongitude: PropTypes.number,
    authId: PropTypes.string,
    secret: PropTypes.string,
    obsBaseUrl: PropTypes.string,
    forecastBaseUrl: PropTypes.string,
    stdParameters: PropTypes.object,
    user: PropTypes.object,
    forecastIconResourceList: PropTypes.string,
    forecastIconSize: PropTypes.string,
    forecastIconSet: PropTypes.string,
    contentMgmtBaseUrl: PropTypes.string,
    getHourlyForecastByLatLon: PropTypes.func,
    getDailyForecastByLatLon: PropTypes.func,
    getCurrentStationObsByLatLon: PropTypes.func,
    showResortView: PropTypes.any
};
